import React from "react";
import { useTranslation } from "react-i18next";
import { ClassCard } from "./ClassCard";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { fetchClasses } from "@/lib/supabase";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export const UpcomingClasses: React.FC = () => {
  const { t } = useTranslation();
  
  // The fetchClasses function now automatically filters out past classes
  const { data: classes, isLoading, error } = useQuery({
    queryKey: ['classes'],
    queryFn: fetchClasses,
  });

  return (
    <section className="py-20 bg-background">
      <div className="container max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">{t('classes.title')}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('classes.subtitle')}
          </p>
        </motion.div>
        
        {error ? (
          <Alert variant="destructive" className="mb-10">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>
              {t('classes.noClassesDesc')}
              <div className="mt-2 text-xs opacity-80">
                {error instanceof Error ? error.message : t('common.error')}
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="border border-border rounded-xl overflow-hidden">
                  <Skeleton className="w-full h-52" />
                  <div className="p-5 space-y-3">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-10 w-full mt-4" />
                  </div>
                </div>
              ))
            ) : classes && classes.length > 0 ? (
              classes.slice(0, 4).map((classItem, index) => (
                <ClassCard key={classItem.id} classData={classItem} index={index} />
              ))
            ) : (
              <div className="col-span-full text-center py-10 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="text-amber-800 font-medium mb-2">{t('classes.noClasses')}</h3>
                <p className="text-amber-700">
                  {t('classes.noClassesDesc')}
                </p>
              </div>
            )}
          </div>
        )}
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/classes">
            <Button size="lg" variant="outline">
              {t('classes.viewAll')}
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
